import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  GetServiceCodeDescription,
  GetMorphologyCodeDescription,
  GetSearchLabCode,
  GetAllClaimPreAuthAllList,
} from "../config/preauthorizationApi"; // Make sure this path is correct

const PreauthorizationDiagnosisNested = () => {
  const dispatch = useDispatch();
  const { preauthorizationRequest, conditionOnset } = useSelector(
    (state) => state.preauthorization
  );
  const { claimRequestData } = useSelector((state) => state.claim);

  const [diagnosisDescriptions, setDiagnosisDescriptions] = useState({});
  const [morphologyDescription, setMorphologyDescription] =
    useState("Loading...");
  const [labTestDescriptions, setLabTestDescriptions] = useState({});

  const cliamPreauthorization = useMemo(
    () =>
      preauthorizationRequest?.entry?.find(
        (e) => e.resource?.resourceType === "Claim"
      )?.resource ||
      claimRequestData?.entry?.find((e) => e.resource?.resourceType === "Claim")
        ?.resource,
    [preauthorizationRequest, claimRequestData]
  );

  useEffect(() => {
    if (conditionOnset?.length === 0) {
      dispatch(GetAllClaimPreAuthAllList());
    }
  }, [conditionOnset]);

  const getVitalSignValue = (targetCode) => {
    const item = cliamPreauthorization?.supportingInfo.find((entry) =>
      entry.category?.coding?.some((coding) => coding.code === targetCode)
    );

    if (item?.valueQuantity) {
      return `${item?.valueQuantity?.value} ${item?.valueQuantity?.code}`;
    } else if (item?.valueDateTime) {
      return `${item?.valueDateTime}`;
    } else if (item?.code) {
      return `${item?.code?.coding[0]?.code}`;
    }
    return "";
  };

  const labTests = useMemo(
    () =>
      Array.isArray(cliamPreauthorization?.supportingInfo)
        ? cliamPreauthorization.supportingInfo.filter(
            (item) => item?.category?.coding?.[0]?.code === "lab-test"
          )
        : [],
    [cliamPreauthorization]
  );

  // 🟩 Fetch Diagnosis Descriptions
  // 🟩 Fetch Diagnosis Descriptions
  const diagnosisArray = useMemo(
    () =>
      (cliamPreauthorization?.diagnosis || [])
        .filter(
          (item) =>
            item?.diagnosisCodeableConcept?.coding?.[0]?.code && item?.sequence
        )
        .reduce((unique, item) => {
          // Remove duplicates based on sequence number
          if (!unique.some((i) => i.sequence === item.sequence)) {
            unique.push(item);
          }
          return unique;
        }, []),
    [cliamPreauthorization]
  );
  // 🟩 Fetch Diagnosis Descriptions
  useEffect(() => {
    const fetchDescriptions = async () => {
      // Filter out items without codes
      const validDiagnosis = diagnosisArray.filter(
        (item) => item?.diagnosisCodeableConcept?.coding?.[0]?.code
      );

      // Get unique codes that need fetching
      const codesNeedingDescription = [
        ...new Set(
          validDiagnosis.map(
            (item) => item.diagnosisCodeableConcept.coding[0].code
          )
        ),
      ].filter((code) => !diagnosisDescriptions[code]);

      if (codesNeedingDescription.length === 0) return;

      try {
        const results = await Promise.all(
          codesNeedingDescription.map((code) =>
            dispatch(GetServiceCodeDescription({ diag_code: code })).unwrap()
          )
        );

        const newDescriptions = {};
        results.forEach((result, index) => {
          const code = codesNeedingDescription[index];
          const descriptionEntry = result?.data?.Diagnosis_code?.find(
            (entry) => entry.icd_code === code
          );
          newDescriptions[code] = descriptionEntry?.icd_description || "N/A";
        });

        setDiagnosisDescriptions((prev) => ({
          ...prev,
          ...newDescriptions,
        }));
      } catch (err) {
        console.error("Error fetching diagnosis descriptions", err);
        const errorDescriptions = {};
        codesNeedingDescription.forEach((code) => {
          errorDescriptions[code] = "Description not found";
        });
        setDiagnosisDescriptions((prev) => ({
          ...prev,
          ...errorDescriptions,
        }));
      }
    };

    if (diagnosisArray.length) {
      fetchDescriptions();
    }
  }, [diagnosisArray, dispatch]); // Only depend on diagnosisArray // REMOVED diagnosisDescriptions from dependencies

  // 🟦 Fetch Morphology Description
  useEffect(() => {
    const fetchMorphologyDescription = async () => {
      const morphCode = getVitalSignValue("morphology");

      if (!morphCode) return;

      try {
        const response = await dispatch(
          GetMorphologyCodeDescription({ morph_code: morphCode })
        ).unwrap();

        const options =
          response.data?.Morphology_Code?.map((item) => ({
            value: item.morphology_code,
            label: item.morphology_shdescription,
          })) || [];

        const match = options.find((opt) => opt.value === morphCode);

        setMorphologyDescription(match?.label || "N/A");
      } catch (error) {
        console.error("Error fetching morphology description:", error);
        setMorphologyDescription("Description not found");
      }
    };

    fetchMorphologyDescription();
  }, [dispatch, cliamPreauthorization]);

  useEffect(() => {
    const fetchLabDescriptions = async () => {
      if (labTests?.length === 0) return;

      for (const test of labTests) {
        const code = test?.code?.coding?.[0]?.code;
        if (code && !labTestDescriptions[code]) {
          try {
            const response = await dispatch(
              GetSearchLabCode({ labcde_id: code })
            ).unwrap();

            const labCode = response?.data?.lab_codes?.find(
              (item) => item.loinc_code === code
            );

            setLabTestDescriptions((prev) => ({
              ...prev,
              [code]: {
                description: labCode?.loinc_description || "N/A",
                unit: labCode?.loinc_uom || "",
              },
            }));
          } catch (error) {
            console.error(`Error fetching lab description for ${code}:`, error);
            setLabTestDescriptions((prev) => ({
              ...prev,
              [code]: {
                description: "Error loading description",
                unit: "",
              },
            }));
          }
        }
      }
    };

    fetchLabDescriptions();
  }, [labTests, labTestDescriptions]);

  return (
    <>
      {/* 🟩 Diagnosis Section */}
      <motion.div
        style={{ border: "2px solid #d9f8eb" }}
        className="rounded p-3 position-relative mt-3 mb-3"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.5 }}
      >
        <div className="position-absolute top-0 start-40 translate-middle-y px-2 d-none d-md-block">
          <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
            <span className="fs-11">Diagnosis</span>
          </span>
        </div>
        <Row className="mt-3">
          {diagnosisArray.map((item, idx) => {
            const code = item?.diagnosisCodeableConcept?.coding?.[0]?.code;
            const condOnset = item?.extension?.find((item) =>
              item?.url?.includes("extension-condition-onset")
            )?.valueCodeableConcept?.coding?.[0]?.code;

            const conditionName = conditionOnset?.find(
              (item) => item?.condonset_code === condOnset
            )?.condonset_name;

            const description = diagnosisDescriptions[code] || "Loading...";
            const onAdmissionCode = item?.onAdmission?.coding?.[0]?.code;
            const onAdmissionValue = onAdmission?.find(
              (item) => item?.code === onAdmissionCode
            )?.value;
            return (
              <Card className="p-3" key={idx}>
                <Row className="g-3">
                  <Col
                    xs={12}
                    md={1}
                    className="d-flex justify-content-center align-items-center"
                  >
                    <div className="category-sevie badge-subtle-success text-success">
                      {item?.sequence}
                    </div>
                  </Col>
                  <Col xs={6} md={2}>
                    <div className="d-flex flex-column">
                      <span className="fs-10 fw-bold">Type</span>
                      <span className="fs-10 fw-normal text-600">
                        {item?.type?.[0]?.coding?.[0]?.code}
                      </span>
                    </div>
                  </Col>
                  <Col xs={6} md={2}>
                    <div className="d-flex flex-column">
                      <span className="fs-10 fw-bold">Condition Onset</span>
                      <span className="fs-10 fw-normal text-600">
                        {conditionName}
                      </span>
                    </div>
                  </Col>
                  <Col xs={6} md={2}>
                    <div className="d-flex flex-column">
                      <span className="fs-10 fw-bold">On Admission</span>
                      <span className="fs-10 fw-normal text-600">
                        {onAdmissionValue}
                      </span>
                    </div>
                  </Col>
                  <Col xs={12} md={5}>
                    <div className="d-flex flex-column">
                      <span className="fs-10 fw-bold">Code/Desc.</span>
                      <span className="fs-10 fw-normal text-600">
                        {`(${code}) - ${description}`}
                      </span>
                    </div>
                  </Col>
                </Row>
              </Card>
            );
          })}
        </Row>
      </motion.div>

      {/* 🟦 Morphology Section */}
      {getVitalSignValue("morphology") && (
        <motion.div
          style={{ border: "2px solid #d9f8eb" }}
          className="rounded p-3 position-relative mt-3 mb-3"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.5 }}
        >
          <div className="position-absolute top-0 start-40 translate-middle-y px-2 d-none d-md-block">
            <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
              <span className="fs-11">Morphology</span>
            </span>
          </div>
          <Row>
            <Col xs={12}>
              <Card className="p-3">
                <Row className="g-3">
                  <Col xs={3}>
                    <div className="d-flex flex-column">
                      <span className="fs-10 fw-bold">Code</span>
                      <span className="fs-10 fw-normal text-300">
                        {getVitalSignValue("morphology")}
                      </span>
                    </div>
                  </Col>
                  <Col xs={9}>
                    <div className="d-flex flex-column">
                      <span className="fs-10 fw-bold">Description</span>
                      <span className="fs-10 fw-normal text-300">
                        {morphologyDescription}
                      </span>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </motion.div>
      )}

      {/* 🧪 Lab Test Section */}
      {labTests && labTests.length > 0 && (
        <motion.div
          style={{ border: "2px solid #e6e6ff" }}
          className="rounded p-3 position-relative mt-3 mb-3"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.5 }}
        >
          <div className="position-absolute top-0 start-40 translate-middle-y px-2 d-none d-md-block">
            <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-primary">
              <span className="fs-11">Lab Tests</span>
            </span>
          </div>
          <Row className="mt-3">
            {labTests.map((test, idx) => {
              const code = test?.code?.coding?.[0]?.code;
              const quantity = test?.valueQuantity?.value;
              const quantityDisplay = quantity ? `${quantity}` : "";
              const { description = "Loading...", unit = "" } =
                labTestDescriptions[code] || {};

              return (
                <Card className="p-3" key={`lab-${idx}`}>
                  <Row className="g-3 align-items-center">
                    <Col xs={12} md={1}>
                      <div className="category-sevie badge-subtle-primary text-primary">
                        {idx + 1}
                      </div>
                    </Col>
                    <Col xs={12} md={6}>
                      <div className="d-flex flex-column">
                        <span className="fs-10 fw-bold">Code/Description</span>
                        <span className="fs-10 fw-normal text-600">
                          {`(${code}) - ${description}`}
                        </span>
                      </div>
                    </Col>
                    <Col xs={12} md={2}>
                      <div className="d-flex flex-column">
                        <span className="fs-10 fw-bold">Unit</span>
                        <span className="fs-10 fw-normal text-600">{unit}</span>
                      </div>
                    </Col>
                    <Col xs={12} md={3}>
                      <div className="d-flex flex-column">
                        <span className="fs-10 fw-bold">Quantity</span>
                        <span className="fs-10 fw-normal text-600">
                          {quantityDisplay}
                        </span>
                      </div>
                    </Col>
                  </Row>
                </Card>
              );
            })}
          </Row>
        </motion.div>
      )}
    </>
  );
};

export default PreauthorizationDiagnosisNested;

const onAdmission = [
  { code: "y", value: "Yes" },
  { code: "n", value: "No" },
  { code: "u", value: "Unknown" },
];
